import React, { useEffect, useState } from "react";
import api from "../api/client";

/**
 * SSO provider admin card — a guided wizard. The common case is dead simple:
 * pick an identity provider (Entra / Google / Okta), type a work email, paste
 * the two values only the IdP can give you (Client ID + secret). AeroGauge
 * derives the discovery URL, the allowed sign-in domain, the scopes, and shows
 * the exact redirect URI to register. SAML and on-prem Active Directory live
 * behind "Other options" so they don't clutter the everyday path.
 *
 * The OIDC client secret is write-only (existing values show as "set", never
 * come back). AD stores no secret (staff bind with their own credentials).
 */

interface SSOProvider {
  id: number;
  name: string;
  protocol: string;
  enabled: boolean;
  oidc_discovery_url: string | null;
  oidc_client_id: string | null;
  oidc_scopes: string;
  saml_idp_entity_id: string | null;
  saml_idp_sso_url: string | null;
  saml_idp_x509_cert: string | null;
  ldap_host: string | null;
  ldap_port: number | null;
  ldap_use_ssl: boolean;
  ldap_start_tls: boolean;
  ldap_base_dn: string | null;
  ldap_bind_dn_template: string | null;
  ldap_domain: string | null;
  audience: string;
  customer_id: number | null;
  customer_name: string | null;
  auto_provision: boolean;
  allowed_email_domains: string;
  allowed_group_ids: string;
  default_role: string;
  has_client_secret: boolean;
}

interface CustomerLite {
  id: number;
  name: string;
}

// The user picks a "method"; we map it to a protocol + fill the derivable bits.
type Method = "entra" | "google" | "okta" | "saml" | "ad" | "custom";

const OIDC_METHODS: Method[] = ["entra", "google", "okta", "custom"];

interface MethodCard {
  key: Method;
  name: string;
  blurb: string;
  tile: string; // tailwind bg for the monogram tile
  glyph: string; // short monogram
}

const IDP_CARDS: MethodCard[] = [
  {
    key: "entra",
    name: "Microsoft Entra ID",
    blurb: "Microsoft 365 / Azure AD",
    tile: "bg-[#0067b8]",
    glyph: "▦",
  },
  { key: "google", name: "Google Workspace", blurb: "Google accounts", tile: "bg-[#ea4335]", glyph: "G" },
  { key: "okta", name: "Okta", blurb: "Okta org", tile: "bg-[#00297a]", glyph: "O" },
];

const ROLE_OPTIONS = ["admin", "technician", "viewer"];

function emailDomain(email: string): string {
  const at = (email || "").indexOf("@");
  return at >= 0 ? email.slice(at + 1).trim().toLowerCase() : "";
}

function normHost(v: string): string {
  return (v || "")
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");
}

/** Derive the .well-known discovery URL from the method + what the user typed. */
function deriveDiscovery(method: Method, email: string, orgUrl: string): string {
  const d = emailDomain(email);
  if (method === "entra")
    return d ? `https://login.microsoftonline.com/${d}/v2.0/.well-known/openid-configuration` : "";
  if (method === "google") return "https://accounts.google.com/.well-known/openid-configuration";
  if (method === "okta") {
    const o = normHost(orgUrl);
    return o ? `https://${o}/.well-known/openid-configuration` : "";
  }
  return ""; // custom → user pastes it
}

function detectMethod(p: SSOProvider): Method {
  if (p.protocol === "saml") return "saml";
  if (p.protocol === "ad") return "ad";
  const url = p.oidc_discovery_url || "";
  if (url.includes("login.microsoftonline.com")) return "entra";
  if (url.includes("accounts.google.com")) return "google";
  if (/\.okta\.com/.test(url)) return "okta";
  return "custom";
}

export default function SSOProvidersCard() {
  const [providers, setProviders] = useState<SSOProvider[]>([]);
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [redirectUri, setRedirectUri] = useState("");
  const [samlAcs, setSamlAcs] = useState("");
  const [samlEntity, setSamlEntity] = useState("");
  // Shared "hosted" apps configured for THIS deployment (operator-provided;
  // nothing built in). When present, Entra/Google setup becomes email-only.
  const [hosted, setHosted] = useState<{ entra: boolean; google: boolean }>({
    entra: false,
    google: false,
  });

  // Wizard/editor state.
  const [mode, setMode] = useState<"list" | "edit">("list");
  const [showHosted, setShowHosted] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [method, setMethod] = useState<Method | null>(null);
  const [audience, setAudience] = useState<"staff" | "customer">("staff");
  const [customerId, setCustomerId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [orgUrl, setOrgUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [hasSecret, setHasSecret] = useState(false);
  const [customDiscovery, setCustomDiscovery] = useState("");
  // Advanced (SAML / AD) — collapsed by default.
  const [saml, setSaml] = useState({ sso_url: "", entity_id: "", cert: "" });
  const [ad, setAd] = useState({ host: "", domain: "", port: "", use_ssl: false, start_tls: false, base_dn: "" });
  const [advanced, setAdvanced] = useState(false);
  const [defaultRole, setDefaultRole] = useState("technician");
  const [autoProvision, setAutoProvision] = useState(true);
  const [allowedDomains, setAllowedDomains] = useState(""); // usually auto from email
  const [allowedGroupIds, setAllowedGroupIds] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Entra one-click auto-setup.
  const [manualEntra, setManualEntra] = useState(false);
  const [autoStatus, setAutoStatus] = useState<"" | "waiting" | "error">("");
  const [autoError, setAutoError] = useState("");

  const load = () => {
    api
      .get("/api/v1/sso/admin/providers")
      .then((r) => setProviders(r.data))
      .catch(() => setProviders([]));
    api
      .get("/api/v1/sso/admin/hosted-apps")
      .then((r) => {
        const m: Record<string, boolean> = {};
        (r.data || []).forEach((h: { kind: string; configured: boolean }) => (m[h.kind] = h.configured));
        setHosted({ entra: !!m.entra, google: !!m.google });
      })
      .catch(() => setHosted({ entra: false, google: false }));
  };
  useEffect(() => {
    load();
    api.get("/api/v1/sso/admin/redirect-uri").then((r) => setRedirectUri(r.data.redirect_uri)).catch(() => {});
    api
      .get("/api/v1/sso/admin/saml-info")
      .then((r) => {
        setSamlAcs(r.data.acs_url);
        setSamlEntity(r.data.sp_entity_id);
      })
      .catch(() => {});
    api.get("/api/v1/customers").then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
  }, []);

  const resetForm = () => {
    setMethod(null);
    setAudience("staff");
    setCustomerId("");
    setName("");
    setEmail("");
    setOrgUrl("");
    setClientId("");
    setClientSecret("");
    setHasSecret(false);
    setCustomDiscovery("");
    setSaml({ sso_url: "", entity_id: "", cert: "" });
    setAd({ host: "", domain: "", port: "", use_ssl: false, start_tls: false, base_dn: "" });
    setAdvanced(false);
    setDefaultRole("technician");
    setAutoProvision(true);
    setAllowedDomains("");
    setAllowedGroupIds("");
    setManualEntra(false);
    setAutoStatus("");
    setAutoError("");
    setError("");
  };

  const openAdd = () => {
    resetForm();
    setEditingId(null);
    setMode("edit");
  };

  const openEdit = (p: SSOProvider) => {
    resetForm();
    const m = detectMethod(p);
    setEditingId(p.id);
    setMethod(m);
    setAudience(p.audience === "customer" ? "customer" : "staff");
    setCustomerId(p.customer_id != null ? String(p.customer_id) : "");
    setName(p.name);
    setClientId(p.oidc_client_id || "");
    setHasSecret(p.has_client_secret);
    setAllowedDomains(p.allowed_email_domains || "");
    setAllowedGroupIds(p.allowed_group_ids || "");
    setDefaultRole(p.default_role);
    setAutoProvision(p.auto_provision);
    setCustomDiscovery(p.oidc_discovery_url || "");
    if (m === "okta") setOrgUrl(normHost((p.oidc_discovery_url || "").replace(/\/\.well-known.*/, "")));
    if (m === "saml")
      setSaml({ sso_url: p.saml_idp_sso_url || "", entity_id: p.saml_idp_entity_id || "", cert: p.saml_idp_x509_cert || "" });
    if (m === "ad")
      setAd({
        host: p.ldap_host || "",
        domain: p.ldap_domain || "",
        port: p.ldap_port != null ? String(p.ldap_port) : "",
        use_ssl: p.ldap_use_ssl,
        start_tls: p.ldap_start_tls,
        base_dn: p.ldap_base_dn || "",
      });
    if (m === "saml" || m === "ad" || m === "custom") setAdvanced(true);
    setMode("edit");
  };

  const isOidc = method !== null && OIDC_METHODS.includes(method);
  // Entra/Google become email-only when a shared app is configured here.
  const hostedFor = (m: Method | null): boolean => (m === "entra" || m === "google") && hosted[m];
  const derivedDiscovery = method === "custom" ? customDiscovery : deriveDiscovery(method || "custom", email, orgUrl);
  const derivedDomain = allowedDomains || emailDomain(email);
  const suggestedName = (() => {
    if (name.trim()) return name.trim();
    const d = emailDomain(email) || normHost(orgUrl);
    if (method === "entra") return d ? `Microsoft Entra ID (${d})` : "Microsoft Entra ID";
    if (method === "google") return d ? `Google Workspace (${d})` : "Google Workspace";
    if (method === "okta") return d ? `Okta (${d})` : "Okta";
    return name.trim();
  })();

  const save = async () => {
    setError("");
    if (!method) return setError("Pick a sign-in method.");
    if (audience === "customer" && !customerId) return setError("Pick which customer this is for.");

    const body: Record<string, unknown> = {
      audience,
      customer_id: audience === "customer" ? Number(customerId) : null,
      auto_provision: autoProvision,
      allowed_group_ids: allowedGroupIds.trim(),
      default_role: defaultRole,
    };

    if (isOidc) {
      const useHosted = hostedFor(method);
      if (method !== "custom" && method !== "okta" && !email.trim())
        return setError("Enter a work email so we can find your tenant.");
      if (method === "okta" && !orgUrl.trim()) return setError("Enter your Okta org URL (e.g. yourorg.okta.com).");
      if (method === "custom" && !customDiscovery.trim()) return setError("Paste the discovery URL.");
      if (!useHosted) {
        if (!clientId.trim()) return setError("Enter the Client ID from your IdP app.");
        if (!editingId && !clientSecret.trim()) return setError("Enter the Client secret from your IdP app.");
      }
      body.protocol = "oidc";
      body.oidc_discovery_url = derivedDiscovery;
      body.oidc_scopes = "openid email profile";
      body.allowed_email_domains = derivedDomain;
      body.name = suggestedName;
      if (useHosted) {
        body.use_hosted_app = true;
      } else {
        body.oidc_client_id = clientId.trim();
        if (clientSecret.trim()) body.oidc_client_secret = clientSecret;
      }
    } else if (method === "saml") {
      if (!saml.sso_url.trim() || !saml.entity_id.trim() || !saml.cert.trim())
        return setError("SAML needs the IdP SSO URL, EntityID, and signing certificate.");
      body.protocol = "saml";
      body.name = name.trim() || "SAML";
      body.saml_idp_sso_url = saml.sso_url.trim();
      body.saml_idp_entity_id = saml.entity_id.trim();
      body.saml_idp_x509_cert = saml.cert.trim();
      body.allowed_email_domains = allowedDomains.trim();
    } else if (method === "ad") {
      if (!ad.host.trim()) return setError("Enter the Domain Controller hostname or IP.");
      body.protocol = "ad";
      body.name = name.trim() || (ad.domain ? `Active Directory (${ad.domain})` : "Active Directory");
      body.ldap_host = ad.host.trim();
      body.ldap_domain = ad.domain.trim();
      body.ldap_port = ad.port.trim() ? Number(ad.port.trim()) : null;
      body.ldap_use_ssl = ad.use_ssl;
      body.ldap_start_tls = ad.start_tls;
      body.ldap_base_dn = ad.base_dn.trim();
      body.ldap_bind_dn_template = "{username}@{domain}";
      body.allowed_email_domains = allowedDomains.trim();
    }

    setSaving(true);
    try {
      if (editingId) await api.put(`/api/v1/sso/admin/providers/${editingId}`, body);
      else await api.post("/api/v1/sso/admin/providers", body);
      setMode("list");
      resetForm();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Entra one-click: download the setup tool, then poll until it posts the app
  // back and the provider is created.
  const startEntraAutoSetup = async () => {
    setAutoError("");
    setError("");
    if (audience === "customer" && !customerId) return setAutoError("Pick which customer this is for.");
    if (!email.trim()) return setAutoError("Enter a work email first.");
    try {
      const { data } = await api.post("/api/v1/sso/admin/entra-setup/start", {
        audience,
        customer_id: audience === "customer" ? Number(customerId) : null,
        allowed_email_domains: emailDomain(email),
        name: suggestedName,
      });
      // Download the .cmd tool.
      const bin = atob(data.script_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || "AeroGauge-SSO-Setup.cmd";
      a.click();
      URL.revokeObjectURL(url);
      setAutoStatus("waiting");
      const token: string = data.token;
      const deadline = Date.now() + 15 * 60 * 1000;
      const poll = async () => {
        if (Date.now() > deadline) {
          setAutoStatus("error");
          setAutoError("Timed out waiting for setup to finish. Run the tool, or configure manually.");
          return;
        }
        try {
          const s = await api.get(
            `/api/v1/sso/admin/entra-setup/status?token=${encodeURIComponent(token)}`,
          );
          if (s.data.status === "done") {
            setMode("list");
            resetForm();
            load();
            return;
          }
        } catch {
          /* keep polling */
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setAutoStatus("error");
      setAutoError(err.response?.data?.detail || "Couldn't start setup.");
    }
  };

  const remove = async (p: SSOProvider) => {
    if (!window.confirm(`Delete SSO provider "${p.name}"? Linked users are unlinked, not deleted.`)) return;
    try {
      await api.delete(`/api/v1/sso/admin/providers/${p.id}`);
      load();
    } catch {
      /* ignore */
    }
  };

  // ── Hosted (shared) app setup ────────────────────────────────────────────────
  if (mode === "list" && showHosted) {
    return (
      <HostedAppsPanel
        redirectUri={redirectUri}
        onClose={() => {
          setShowHosted(false);
          load();
        }}
      />
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  if (mode === "list") {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Single sign-on</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Let staff or a customer sign in with Microsoft, Google, Okta, or your own directory.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHosted(true)}
              className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700/40"
              title="Configure a shared Microsoft/Google app so future setups are email-only"
            >
              Sign-in apps
              {(hosted.entra || hosted.google) && (
                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 align-middle" />
              )}
            </button>
            <button
              onClick={openAdd}
              className="text-xs px-3 py-1.5 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark"
            >
              + Add sign-in
            </button>
          </div>
        </div>

        {providers.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic py-6 text-center">No sign-in methods yet.</p>
        ) : (
          <div className="space-y-2">
            {providers.map((p) => (
              <div key={p.id} className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{p.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {p.audience === "customer" ? `Customer${p.customer_name ? `: ${p.customer_name}` : ""}` : "Staff"}
                    {" · "}
                    {p.enabled ? "Enabled" : "Disabled"}
                    {" · "}
                    {p.allowed_email_domains || "any domain"}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => openEdit(p)} className="text-xs text-brand dark:text-brand-light hover:underline font-medium">
                    Edit
                  </button>
                  <button onClick={() => remove(p)} className="text-xs text-red-600 dark:text-red-400 hover:underline font-medium">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Wizard / editor ──────────────────────────────────────────────────────────
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{editingId ? "Edit sign-in" : "Add sign-in"}</h2>
        <button
          onClick={() => {
            setMode("list");
            resetForm();
          }}
          className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          Cancel
        </button>
      </div>

      {/* Who is this for */}
      <div className="mb-4">
        <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Who signs in with this?</div>
        <div className="flex gap-2">
          {(["staff", "customer"] as const).map((a) => (
            <button
              key={a}
              onClick={() => setAudience(a)}
              className={`flex-1 text-sm rounded-lg border px-3 py-2 transition-colors ${
                audience === a ? "border-brand bg-brand/5 text-brand dark:text-brand-light font-medium" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              }`}
            >
              {a === "staff" ? "My team (staff console)" : "A customer (their portal)"}
            </button>
          ))}
        </div>
        {audience === "customer" && (
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="mt-2 w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 bg-white"
          >
            <option value="">— Select a customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Pick a method (hidden once chosen, unless custom/advanced) */}
      {method === null && (
        <>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Choose a sign-in provider</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {IDP_CARDS.map((c) => (
              <button
                key={c.key}
                onClick={() => setMethod(c.key)}
                className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-3 hover:border-brand hover:bg-brand/5 transition-colors text-left"
              >
                <span className={`w-9 h-9 rounded-lg ${c.tile} text-white flex items-center justify-center text-lg font-bold shrink-0`}>
                  {c.glyph}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{c.name}</span>
                  <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate">{c.blurb}</span>
                </span>
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              setMethod("saml");
              setAdvanced(true);
            }}
            className="mt-3 text-xs text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand-light"
          >
            Other options: SAML or on-prem Active Directory →
          </button>
        </>
      )}

      {/* The chosen method's minimal fields */}
      {method !== null && (
        <div className="space-y-3">
          {/* chosen-provider chip + change */}
          <div className="flex items-center justify-between bg-slate-50 dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {method === "entra" && "Microsoft Entra ID"}
              {method === "google" && "Google Workspace"}
              {method === "okta" && "Okta"}
              {method === "custom" && "Custom OpenID Connect"}
              {method === "saml" && "SAML 2.0"}
              {method === "ad" && "On-prem Active Directory"}
            </span>
            <button onClick={() => setMethod(null)} className="text-xs text-brand dark:text-brand-light hover:underline">
              Change
            </button>
          </div>

          {/* OIDC (entra/google/okta/custom) */}
          {isOidc && (
            <>
              {(method === "entra" || method === "google") && (
                <Field label="Your work email">
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={method === "entra" ? "you@company.com" : "you@company.com"}
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    type="email"
                    autoFocus
                  />
                  {emailDomain(email) && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                      We'll allow <b>{emailDomain(email)}</b> accounts and find your tenant automatically.
                    </p>
                  )}
                </Field>
              )}
              {method === "okta" && (
                <Field label="Your Okta org URL">
                  <input
                    value={orgUrl}
                    onChange={(e) => setOrgUrl(e.target.value)}
                    placeholder="yourorg.okta.com"
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                    autoFocus
                  />
                  <div className="mt-2">
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Optional: a work email to restrict the domain"
                      className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      type="email"
                    />
                  </div>
                </Field>
              )}
              {method === "custom" && (
                <Field label="OpenID Connect discovery URL">
                  <input
                    value={customDiscovery}
                    onChange={(e) => setCustomDiscovery(e.target.value)}
                    placeholder="https://idp.example.com/.well-known/openid-configuration"
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                  />
                </Field>
              )}

              {hostedFor(method) ? (
                /* A shared app is configured for this deployment → nothing to paste. */
                <div className="text-xs bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 text-emerald-800 dark:text-emerald-300">
                  Uses this system's shared {method === "entra" ? "Microsoft" : "Google"} sign-in
                  app — nothing to paste. Just confirm the email above and enable.
                </div>
              ) : (
                <>
                  {/* Entra: one-click auto-setup is the primary path. */}
                  {method === "entra" && !editingId && (
                    <div className="space-y-2">
                      <div className="text-xs bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 text-emerald-800 dark:text-emerald-300">
                        <b>Automatic setup.</b> Download a small tool, run it, and sign in as a
                        Microsoft admin. AeroGauge registers the app, redirect URI, and secret for
                        you and turns sign-in on — nothing to paste. The tool also lets you{" "}
                        <b>pick which security group</b> may sign in, so it's not open to everyone.
                      </div>
                      <button
                        type="button"
                        onClick={startEntraAutoSetup}
                        disabled={autoStatus === "waiting"}
                        className="w-full text-sm px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50"
                      >
                        {autoStatus === "waiting" ? "Waiting for setup to finish…" : "Set up automatically"}
                      </button>
                      {autoStatus === "waiting" && (
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">
                          Run the downloaded <b>AeroGauge-SSO-Setup</b> tool and sign in as a Microsoft
                          admin. This page finishes on its own when it's done.
                        </p>
                      )}
                      {autoStatus === "error" && (
                        <p className="text-xs text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                          {autoError}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => setManualEntra(!manualEntra)}
                        className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand-light"
                      >
                        {manualEntra ? "Hide manual setup" : "Prefer to paste the values yourself?"}
                      </button>
                    </div>
                  )}

                  {/* Manual OIDC (Google/Okta/custom always; Entra only via the toggle or on edit). */}
                  {(method !== "entra" || manualEntra || editingId) && (
                    <>
                      {redirectUri && (
                        <div className="text-xs bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                          <div className="font-medium text-blue-900 dark:text-blue-200 mb-1">1. Register this redirect URI at your provider</div>
                          <code className="font-mono text-blue-900 dark:text-blue-200 break-all block bg-white/60 dark:bg-gray-900/60 rounded px-2 py-1">{redirectUri}</code>
                          <div className="text-blue-800 dark:text-blue-300 mt-1.5">
                            {method === "entra" &&
                              "Entra → App registrations → New registration → add this as a Web redirect URI. Then Certificates & secrets → New client secret → copy the secret Value (not the ID)."}
                            {method === "google" &&
                              "Google Cloud → APIs & Services → Credentials → OAuth client ID (Web) → add this as an Authorized redirect URI. Copy the client ID + secret."}
                            {method === "okta" &&
                              "Okta → Applications → Create App Integration → OIDC Web app → Sign-in redirect URI. Copy the Client ID + secret."}
                            {method === "custom" && "Add this redirect URI to your IdP's app, then create a client secret."}
                          </div>
                          {method === "google" && (
                            <div className="text-blue-700 dark:text-blue-300 mt-1.5">
                              Tip: set this up once under <b>Sign-in apps</b> and every future Google
                              sign-in becomes email-only.
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 -mb-1">2. Paste the two values from that app</div>
                      <Field label="Client ID">
                        <input
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                        />
                      </Field>
                      <Field label={editingId && hasSecret ? "Client secret (leave blank to keep existing)" : "Client secret"}>
                        <input
                          type="password"
                          value={clientSecret}
                          onChange={(e) => setClientSecret(e.target.value)}
                          autoComplete="new-password"
                          className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                        />
                      </Field>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* SAML (advanced) */}
          {method === "saml" && (
            <>
              <Field label="IdP SSO URL">
                <input
                  value={saml.sso_url}
                  onChange={(e) => setSaml({ ...saml, sso_url: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                  placeholder="https://<org>.okta.com/app/<id>/sso/saml"
                />
              </Field>
              <Field label="IdP EntityID (Issuer)">
                <input
                  value={saml.entity_id}
                  onChange={(e) => setSaml({ ...saml, entity_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                />
              </Field>
              <Field label="IdP signing certificate (PEM or base64)">
                <textarea
                  value={saml.cert}
                  onChange={(e) => setSaml({ ...saml, cert: e.target.value })}
                  rows={4}
                  className="w-full border rounded-lg px-3 py-2 text-xs font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="-----BEGIN CERTIFICATE-----&#10;MIID...&#10;-----END CERTIFICATE-----"
                />
              </Field>
              {samlAcs && (
                <div className="text-xs bg-slate-50 dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700 rounded-lg p-3 space-y-1">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Reply (ACS) URL:</span>{" "}
                    <code className="font-mono text-gray-800 dark:text-gray-200 break-all">{samlAcs}</code>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">SP EntityID:</span>{" "}
                    <code className="font-mono text-gray-800 dark:text-gray-200 break-all">{samlEntity}</code>
                  </div>
                </div>
              )}
            </>
          )}

          {/* On-prem AD (advanced) */}
          {method === "ad" && (
            <>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5">
                Staff sign in with their own AD username &amp; password — no service account is stored.
              </div>
              <Field label="Domain Controller (hostname or IP)">
                <input
                  value={ad.host}
                  onChange={(e) => setAd({ ...ad, host: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                  placeholder="dc01.corp.local"
                />
              </Field>
              <Field label="Default domain">
                <input
                  value={ad.domain}
                  onChange={(e) => setAd({ ...ad, domain: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                  placeholder="corp.local"
                />
              </Field>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={ad.use_ssl} onChange={(e) => setAd({ ...ad, use_ssl: e.target.checked })} />
                  LDAPS
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={ad.start_tls} onChange={(e) => setAd({ ...ad, start_tls: e.target.checked })} />
                  StartTLS
                </label>
              </div>
              <details className="text-xs text-gray-500 dark:text-gray-400">
                <summary className="cursor-pointer">Advanced (base DN, port)</summary>
                <div className="mt-2 space-y-2">
                  <input
                    value={ad.base_dn}
                    onChange={(e) => setAd({ ...ad, base_dn: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                    placeholder="Search base DN (optional) — DC=corp,DC=local"
                  />
                  <input
                    value={ad.port}
                    onChange={(e) => setAd({ ...ad, port: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                    placeholder="Port (blank = 389, or 636 for LDAPS)"
                    inputMode="numeric"
                  />
                </div>
              </details>
            </>
          )}

          {/* Method switcher for the advanced trio */}
          {advanced && (
            <div className="flex gap-3 text-xs pt-1">
              <button onClick={() => setMethod("saml")} className={method === "saml" ? "text-brand dark:text-brand-light font-medium" : "text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand-light"}>
                SAML
              </button>
              <button onClick={() => setMethod("ad")} className={method === "ad" ? "text-brand dark:text-brand-light font-medium" : "text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand-light"}>
                On-prem AD
              </button>
              <button onClick={() => setMethod("custom")} className={method === "custom" ? "text-brand dark:text-brand-light font-medium" : "text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand-light"}>
                Custom OIDC
              </button>
            </div>
          )}

          {/* Tidy advanced knobs shared across methods */}
          <details className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2">
            <summary className="cursor-pointer">Name &amp; provisioning options</summary>
            <div className="mt-2 space-y-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`Display name (default: ${suggestedName || "auto"})`}
                className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              />
              {(method === "saml" || method === "ad" || method === "custom") && (
                <input
                  value={allowedDomains}
                  onChange={(e) => setAllowedDomains(e.target.value)}
                  placeholder="Allowed email domains (CSV, blank = any)"
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              )}
              {isOidc && (
                <div>
                  <input
                    value={allowedGroupIds}
                    onChange={(e) => setAllowedGroupIds(e.target.value)}
                    placeholder="Restrict to security group IDs (optional, comma-separated)"
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                  />
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    Only members of these IdP groups may sign in. Blank = anyone in the allowed
                    domain. For Microsoft, the automatic setup lets you pick the group — or paste a
                    group's Object ID here. (Google doesn't provide group membership in sign-in, so
                    group limits apply to Microsoft/OIDC.)
                  </p>
                </div>
              )}
              {audience === "staff" && (
                <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  New-user role:
                  <select value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)} className="border rounded-lg px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100">
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={autoProvision} onChange={(e) => setAutoProvision(e.target.checked)} />
                Auto-create new users on first sign-in
              </label>
            </div>
          </details>

          {error && <p className="text-xs text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">{error}</p>}

          {/* The bottom "Enable" button drives the manual/other paths. In Entra
              auto-mode the "Set up automatically" button above completes the flow,
              so we hide this to avoid a dead click. */}
          {!(method === "entra" && !hostedFor("entra") && !editingId && !manualEntra) && (
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={save}
                disabled={saving}
                className="text-sm px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50"
              >
                {saving ? "Saving…" : editingId ? "Save changes" : "Enable sign-in"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

interface HostedRow {
  kind: string;
  configured: boolean;
  client_id: string | null;
}

const HOSTED_META: Record<string, { name: string; register: string }> = {
  entra: {
    name: "Microsoft (Entra ID)",
    register:
      "Entra admin center → App registrations → New registration → Multitenant → add the redirect URI below → create a client secret. Paste the Application (client) ID + secret.",
  },
  google: {
    name: "Google Workspace",
    register:
      "Google Cloud console → APIs & Services → Credentials → OAuth client ID (Web) → add the redirect URI below. Paste the client ID + secret.",
  },
};

/**
 * One-time setup for the deployment's OWN shared sign-in apps. Nothing is
 * built in — these are blank until the operator of this instance registers
 * their own Microsoft / Google app and pastes its credentials. Once set,
 * everyday Entra/Google SSO becomes email-only.
 */
function HostedAppsPanel({ redirectUri, onClose }: { redirectUri: string; onClose: () => void }) {
  const [rows, setRows] = useState<HostedRow[]>([]);
  const [draft, setDraft] = useState<Record<string, { client_id: string; client_secret: string }>>({});
  const [busy, setBusy] = useState<string>("");
  const [error, setError] = useState("");

  const load = () => {
    api
      .get("/api/v1/sso/admin/hosted-apps")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  };
  useEffect(() => {
    load();
  }, []);

  const setField = (kind: string, k: "client_id" | "client_secret", v: string) =>
    setDraft((d) => ({
      ...d,
      [kind]: { ...(d[kind] || { client_id: "", client_secret: "" }), [k]: v },
    }));

  const saveKind = async (kind: string) => {
    setError("");
    const d = draft[kind] || { client_id: "", client_secret: "" };
    if (!d.client_id.trim() || !d.client_secret.trim()) {
      setError("Enter both the Client ID and secret.");
      return;
    }
    setBusy(kind);
    try {
      await api.put(`/api/v1/sso/admin/hosted-apps/${kind}`, {
        client_id: d.client_id.trim(),
        client_secret: d.client_secret,
      });
      setDraft((x) => ({ ...x, [kind]: { client_id: "", client_secret: "" } }));
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy("");
    }
  };

  const removeKind = async (kind: string) => {
    if (!window.confirm(`Remove the shared ${HOSTED_META[kind].name} app? New setups will go back to pasting a client ID + secret.`))
      return;
    setBusy(kind);
    try {
      await api.delete(`/api/v1/sso/admin/hosted-apps/${kind}`);
      load();
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Shared sign-in apps</h2>
        <button onClick={onClose} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
          ← Back
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Register your own Microsoft and Google app once for this system. After that, adding a
        Microsoft or Google sign-in is just an email address — no client ID or secret to paste each
        time. Leave these blank to keep pasting per provider.
      </p>

      {redirectUri && (
        <div className="text-xs bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
          <span className="text-blue-900 dark:text-blue-200">Redirect URI to register on both apps:</span>{" "}
          <code className="font-mono text-blue-900 dark:text-blue-200 break-all">{redirectUri}</code>
        </div>
      )}

      <div className="space-y-3">
        {["entra", "google"].map((kind) => {
          const row = rows.find((r) => r.kind === kind);
          const d = draft[kind] || { client_id: "", client_secret: "" };
          return (
            <div key={kind} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{HOSTED_META[kind].name}</div>
                {row?.configured ? (
                  <span className="text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 rounded-full px-2 py-0.5">
                    Configured
                  </span>
                ) : (
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">Not set</span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">{HOSTED_META[kind].register}</p>
              {row?.configured && row.client_id && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                  Current Client ID: <code className="font-mono">{row.client_id}</code>
                </p>
              )}
              <div className="space-y-2">
                <input
                  value={d.client_id}
                  onChange={(e) => setField(kind, "client_id", e.target.value)}
                  placeholder={row?.configured ? "New Client ID (to replace)" : "Client ID"}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  value={d.client_secret}
                  onChange={(e) => setField(kind, "client_secret", e.target.value)}
                  placeholder={row?.configured ? "New client secret (to replace)" : "Client secret"}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 font-mono"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => saveKind(kind)}
                    disabled={busy === kind}
                    className="text-xs px-3 py-1.5 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50"
                  >
                    {busy === kind ? "Saving…" : row?.configured ? "Replace" : "Save"}
                  </button>
                  {row?.configured && (
                    <button
                      onClick={() => removeKind(kind)}
                      disabled={busy === kind}
                      className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mt-3">{error}</p>
      )}
    </div>
  );
}
